import { useCallback, useEffect, useState } from 'react';
import { submitChecklist } from '../api/client';
import { readOutbox, removeFromOutbox } from '../storage/outbox';

export function useOutboxSync() {
  const [pendingCount, setPendingCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncError, setLastSyncError] = useState<string | null>(null);
  const [pendingSince, setPendingSince] = useState<string | null>(null);

  const syncOutbox = useCallback(async () => {
    const entries = await readOutbox();
    setPendingCount(entries.length);
    const oldest = entries.reduce<string | null>((acc, entry) => {
      if (!entry.queuedAt) return acc;
      if (!acc) return entry.queuedAt;
      return new Date(entry.queuedAt) < new Date(acc) ? entry.queuedAt : acc;
    }, null);
    setPendingSince(oldest);
    if (entries.length === 0 || !navigator.onLine) {
      return;
    }

    setIsSyncing(true);
    setLastSyncError(null);

    for (const entry of entries) {
      try {
        await submitChecklist(entry.payload);
        if (entry.id != null) {
          await removeFromOutbox(entry.id);
        }
      } catch (error) {
        console.warn('Outbox sync failed', error);
        setLastSyncError((error as Error).message ?? 'Unable to sync queued submissions');
        break;
      }
    }

    const remaining = await readOutbox();
    setPendingCount(remaining.length);
    const remainingOldest = remaining.reduce<string | null>((acc, entry) => {
      if (!entry.queuedAt) return acc;
      if (!acc) return entry.queuedAt;
      return new Date(entry.queuedAt) < new Date(acc) ? entry.queuedAt : acc;
    }, null);
    setPendingSince(remainingOldest);
    setIsSyncing(false);
  }, []);

  useEffect(() => {
    syncOutbox();
    const handleOnline = () => syncOutbox();
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [syncOutbox]);

  return { pendingCount, isSyncing, lastSyncError, pendingSince, syncOutbox };
}
